import { useSourcingExternalStore } from 'modules/sourcing-external/stores';
import { SourcingSharedReviewInvitationsStep } from 'modules/sourcing-shared/shared-steps';
import { getSourcingExternalHeaderBreadcrumbs } from 'modules/sourcing-external/utils/getSourcingExternalHeaderBreadcrumbs';
import { useShallow } from 'zustand/react/shallow';
import {
  useUpdateSourcingCandidateMutation,
  useGenerateCandidateRelevanceMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useCallback } from 'react';
import { SourcingSharedCandidateBatch } from 'modules/sourcing-shared/shared-stores/types';
import {
  SourcingSharedCustomizeMessageEmailFormData,
  SourcingSharedCustomizeMessageSmsFormData
} from 'modules/sourcing-shared/forms';
import { Channels } from 'generated-graphql/types';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

export const SourcingExternalReviewInvitationsStep = () => {
  const {
    selectedCustomizeCandidateId,
    candidateBatch,
    channelFormData,
    applicationFormData,
    jobInformationFormData,
    customizeMessageFormData,
    selectedRows,
    editedOpeningMessage,
    setSelectedCustomizeCandidateId,
    onReviewInvitationsBack,
    onInviteSubmit,
    updateSelectedRowCustomMessage,
    setStep
  } = useSourcingExternalStore(
    useShallow((state) => ({
      selectedRows: state.selectedRows,
      selectedCustomizeCandidateId: state.selectedCustomizeCandidateId,
      candidateBatch: state.candidateBatch,
      channelFormData: state.channelFormData,
      applicationFormData: state.applicationFormData,
      jobInformationFormData: state.jobInformationFormData,
      customizeMessageFormData: state.customizeMessageFormData,
      editedOpeningMessage: state.editedOpeningMessage,
      setSelectedCustomizeCandidateId: state.setSelectedCustomizeCandidateId,
      onReviewInvitationsBack: state.onReviewInvitationsBack,
      onInviteSubmit: state.onInviteSubmit,
      updateSelectedRowCustomMessage: state.updateSelectedRowCustomMessage,
      setStep: state.setStep
    }))
  );

  const [updateSourcingCandidate] = useUpdateSourcingCandidateMutation();
  const [generateCandidateRelevance, { loading: isGeneratingRelevance }] =
    useGenerateCandidateRelevanceMutation();

  const handleUpdateInvitationMessage = useCallback(
    async (candidateId: string, customMessage: string) => {
      await updateSourcingCandidate({
        variables: {
          input: {
            id: candidateId,
            customInvitationMessage: customMessage
          }
        }
      });

      updateSelectedRowCustomMessage(candidateId, customMessage);
    },
    [updateSourcingCandidate, updateSelectedRowCustomMessage]
  );

  const handleGenerateRelevance = useCallback(
    async (
      candidateId: string,
      candidateBatch: SourcingSharedCandidateBatch,
      customizeMessageFormData:
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
    ): Promise<string> => {
      const { data } = await generateCandidateRelevance({
        variables: {
          input: {
            customTemplatedMessage:
              customizeMessageFormData?.customTemplatedMessage ?? '',
            employerName:
              candidateBatch?.jobDescriptionParsed?.employerName || '',
            jobDescriptionText: candidateBatch?.jobDescription || '',
            jobTitle: candidateBatch?.jobDescriptionParsed?.jobTitle ?? '',
            sourcedCandidateId: candidateId,
            channel: channelFormData?.channel
          }
        }
      });

      return data?.generateCandidateRelevance?.candidateRelevance || '';
    },
    [generateCandidateRelevance]
  );

  if (
    !candidateBatch ||
    !customizeMessageFormData ||
    !jobInformationFormData ||
    !applicationFormData
  )
    return null;

  return (
    <SourcingSharedReviewInvitationsStep
      channel={channelFormData?.channel ?? Channels.Email}
      headerBreadcrumbs={getSourcingExternalHeaderBreadcrumbs({
        candidateBatchJobTitle: getAnalysisTitle(candidateBatch),
        candidateBatchId: candidateBatch?.id ?? '',
        setStep
      })}
      candidateItems={selectedRows.map((row) => ({
        id: row.id,
        fullName: row.fullName,
        location: row.location ?? '',
        title: row.title ?? '',
        customInvitationMessage: row.customInvitationMessage ?? '',
        profile: {
          id: row.id,
          fullName: row.fullName,
          linkedInProfileUrl: row.linkedInProfileUrl ?? '',
          location: row.location ?? '',
          title: row.title ?? '',
          source: row.source,
          skills: row.skills?.filter((skill) => skill !== null) ?? [],
          education:
            row.education?.filter((education) => education !== null) ?? [],
          experience:
            row.experience?.filter((experience) => experience !== null) ?? []
        }
      }))}
      selectedCandidateId={
        selectedCustomizeCandidateId ?? selectedRows[0]?.id ?? ''
      }
      candidateBatch={candidateBatch}
      customizeMessageFormData={customizeMessageFormData}
      jobInformationFormData={jobInformationFormData}
      applicationFormData={applicationFormData}
      editedOpeningMessage={editedOpeningMessage}
      setSelectedCandidateId={setSelectedCustomizeCandidateId}
      onReviewInvitationsBack={onReviewInvitationsBack}
      onInviteSubmit={async () =>
        await onInviteSubmit({
          // External sourcing we use sourced candidates ids
          type: 'external',
          sourcedCandidatesIds: selectedRows.map((row) => row.id)
        })
      }
      onUpdateInvitationMessage={handleUpdateInvitationMessage}
      onGenerateRelevance={handleGenerateRelevance}
      isGeneratingRelevance={isGeneratingRelevance ?? false}
    />
  );
};
